import React from 'react';
import { Button, Checkbox, Link } from '@amzn/awsui-components-core';
import { EmployeeCardNodeProps } from '../../types/orgChart.types';
import { EmployeeListItem } from '../../../ea-huddle-v2/components/attendees/EmployeeListItem';
import { convertOrgChartEmployeeToEmployee } from '../../utils/employeeTypeAdapters';
import './EmployeeCardNode.scss';

const ATOZ_PERFORMANCE_URL = 'https://atoz.amazon.work/manager/profiles';
const ATOZ_EMPLOYEE_DETAILS_URL = 'https://atoz.amazon.work/manager/profiles';
/** AtoZ Annual Review "Leadership Principles" feedback (manager view), keyed by the report's alias. */
const ATOZ_ANNUAL_REVIEW_URL = 'https://atoz.amazon.work/feedback/overview/managerView';

export const EmployeeCardNode: React.FC<EmployeeCardNodeProps> = ({
  employee,
  isExpanded,
  isLeader,
  isSelected,
  depth,
  hasChildren,
  onExpand,
  onCollapse,
  onToggleSelection,
  onClick
}) => {
  const displayName = `${employee.firstName} ${employee.lastName}`.trim();

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick();
    }
  };

  const handleExpandCollapse = (event: CustomEvent) => {
    event.stopPropagation();
    if (isExpanded) {
      onCollapse();
    } else {
      onExpand();
    }
  };

  const handleToggleSelection = () => {
    onToggleSelection();
  };

  // Open AtoZ performance page in new tab
  const handlePerformanceClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    window.open(`${ATOZ_PERFORMANCE_URL}/${employee.login}/performance`, '_blank', 'noopener,noreferrer');
  };

  // Open AtoZ employee details page in new tab
  const handleEmployeeDetailsClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    window.open(`${ATOZ_EMPLOYEE_DETAILS_URL}/${employee.login}/employee-details`, '_blank', 'noopener,noreferrer');
  };

  // Open AtoZ Annual Review leadership-principles feedback (manager view) in new tab
  const handleAnnualReviewClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    window.open(`${ATOZ_ANNUAL_REVIEW_URL}/${employee.login}/leadershipPrinciples`, '_blank', 'noopener,noreferrer');
  };

  const containerClassName = ['employee-card-node', onClick ? 'employee-card-node--clickable' : '', isLeader ? 'employee-card-node--leader' : '']
    .filter(Boolean)
    .join(' ');

  // Calculate indentation based on depth (24px per level)
  const indentStyle = {
    paddingLeft: `${depth * 24}px`
  };

  return (
    <div
      className={containerClassName}
      style={indentStyle}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? 'button' : undefined}
    >
      {/* Expand/Collapse Button */}
      <div className="employee-card-node__expand-area">
        {hasChildren ? (
          <Button
            variant="icon"
            iconName={isExpanded ? 'angle-down' : 'angle-right'}
            onClick={handleExpandCollapse}
            ariaLabel={isExpanded ? 'Collapse' : 'Expand'}
          />
        ) : (
          <div className="employee-card-node__expand-placeholder" />
        )}
      </div>

      {/* Employee Info */}
      <div className="employee-card-node__info">
        {/* Selection checkbox - hidden for leader */}
        {!isLeader && (
          <div className="employee-card-node__select">
            <Checkbox checked={isSelected} onChange={handleToggleSelection} ariaLabel={`Select ${displayName} for baseball card generation`} />
          </div>
        )}
        {/* Reuse the shared Huddle+ avatar (name + [alias] + Slack, avatar, details line) so the
            tree rows match the leader card at the top of the page. `levelInlineBeforeCity` hides
            the left level badge and renders `L{level}` right before the city in the details line;
            `showLevel={false}` is redundant with that but kept explicit for intent. */}
        <EmployeeListItem employee={convertOrgChartEmployeeToEmployee(employee)} showLevel={false} levelInlineBeforeCity disableHover />
      </div>

      {/* Actions - Performance, Employee Details links */}
      <div className="employee-card-node__actions">
        <Link
          href={`${ATOZ_PERFORMANCE_URL}/${employee.login}/performance`}
          fontSize="body-s"
          onFollow={(e) => {
            e.preventDefault();
            handlePerformanceClick(e as unknown as React.MouseEvent);
          }}
        >
          Performance
        </Link>
        <Link
          href={`${ATOZ_EMPLOYEE_DETAILS_URL}/${employee.login}/employee-details`}
          fontSize="body-s"
          onFollow={(e) => {
            e.preventDefault();
            handleEmployeeDetailsClick(e as unknown as React.MouseEvent);
          }}
        >
          Employee Details
        </Link>
        <Link
          href={`${ATOZ_ANNUAL_REVIEW_URL}/${employee.login}/leadershipPrinciples`}
          fontSize="body-s"
          onFollow={(e) => {
            e.preventDefault();
            handleAnnualReviewClick(e as unknown as React.MouseEvent);
          }}
        >
          Annual Review
        </Link>
      </div>
    </div>
  );
};

export default EmployeeCardNode;
